import React from "react";
import { ShieldCheck } from "lucide-react";
import "./PatientChatShared.scss";

/** System `payload.type` values rendered as bid ledger cards (opening / reverse / counter). */
export const BID_LEDGER_PAYLOAD_TYPES = new Set(["initial_bid", "reverse_bid", "patient_counter"]);

/** System types that should use the bid/booking timeline layout (intro strip + ledger column). */
export const PATIENT_CHAT_TIMELINE_TYPES = new Set([
  ...BID_LEDGER_PAYLOAD_TYPES,
  "slot_held",
  "appointment_requested",
  "patient_booking_updated",
  "doctor_accept_appointment",
  "doctor_reschedule",
  "admin_reschedule",
  "appointment_approved",
  "appointment_completed",
  "appointment_visit_outcome",
  "treatment_bill_submitted",
  "treatment_bill_patient_response",
]);

export function toDisplayAmount(n) {
  if (n == null || n === "") return null;
  if (typeof n === "number" && Number.isFinite(n)) return n;
  const x = Number(String(n).replace(/[,，\s]/g, ""));
  return Number.isFinite(x) ? x : null;
}

export function fmtINR(n) {
  const x = toDisplayAmount(n);
  if (x == null) return "—";
  return `Rs ${x.toLocaleString("en-IN")}`;
}

/** Emphasises the numeric portion of INR in cards and lists. */
function InrDisplayParts({ amount, currencyClassName, figureClassName }) {
  const s = fmtINR(amount);
  if (s === "—") return s;
  const m = /^Rs\s+(.+)$/.exec(s);
  if (!m) return s;
  return (
    <>
      <span className={currencyClassName}>Rs</span>
      <span className={figureClassName}>{m[1]}</span>
    </>
  );
}

/** Normalize Mongo id from JSON (hex string, $oid wrapper, or ObjectId-like). */
export function extractMongoId(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    const t = value.trim();
    if (!t || t === "[object Object]") return "";
    return /^[a-f\d]{24}$/i.test(t) ? t : "";
  }
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return String(Math.floor(value));
  }
  if (typeof value === "object") {
    if (typeof value.$oid === "string" && /^[a-f\d]{24}$/i.test(value.$oid)) return value.$oid;
    if (typeof value.toHexString === "function") {
      try {
        const hx = String(value.toHexString());
        if (/^[a-f\d]{24}$/i.test(hx)) return hx;
      } catch (_) {}
    }
  }
  return "";
}

function bidLogRowTime(b) {
  const t = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
  return Number.isFinite(t) ? t : 0;
}

function pickLatestBidLogRow(rows) {
  if (!Array.isArray(rows) || !rows.length) return null;
  return rows.reduce((best, b) => (bidLogRowTime(b) >= bidLogRowTime(best) ? b : best));
}

/**
 * Bid log row used for booking and procedure copy (matches PatientChatsHubBidBookingPanel).
 */
export function pickRowForBooking(bidHistory) {
  if (!Array.isArray(bidHistory) || !bidHistory.length) return null;
  const agreed = bidHistory.filter(
    (b) =>
      b &&
      ((String(b.providerAction) === "accepted_patient_price" && String(b.patientAction) === "accepted") ||
        (String(b.providerAction) === "countered" && String(b.patientAction) === "accepted"))
  );
  if (agreed.length) return pickLatestBidLogRow(agreed);
  return bidHistory[bidHistory.length - 1];
}

export function procedureLabelFromBidLogRow(b) {
  if (!b) return "";
  return [b.categoryName, b.subCategoryName].filter(Boolean).join(" · ");
}

export function procedureLabelFromBidHistory(bidHistory) {
  return procedureLabelFromBidLogRow(pickRowForBooking(bidHistory));
}

function formatShortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

function systemHeadline(type, payload) {
  const p = payload || {};
  const t = String(type || "");
  if (t === "appointment_visit_outcome" && p.patientAttended === true && p.procedureProceeding === true) {
    return "Visit completed";
  }
  const map = {
    initial_bid: "Opening bid",
    reverse_bid: "Suggested amount",
    patient_accept: "Offer accepted",
    patient_reject: "Offer declined",
    patient_counter: "Your counter-offer",
    provider_accept: "Provider accepted",
    provider_reject: "Provider declined",
    appointment_requested: "Visit requested",
    patient_booking_updated: "Visit time updated",
    doctor_accept_appointment: "Provider update",
    doctor_reschedule: "New time proposed",
    admin_reschedule: "Admin proposed time",
    appointment_approved: "Visit confirmed",
    appointment_completed: "Visit completed",
    appointment_visit_outcome: "Visit outcome",
    slot_held: "Visit time (reference)",
  };
  return map[t] || "Update";
}

function systemStateLabel(type, providerLabel = "Doctor") {
  if (type === "patient_accept") return "Accepted by you";
  if (type === "provider_accept") return `Accepted by ${providerLabel}`;
  if (type === "patient_reject") return "Declined by you";
  if (type === "provider_reject") return `Declined by ${providerLabel}`;
  if (type === "appointment_requested") return "Awaiting review";
  if (type === "patient_booking_updated") return "Awaiting review";
  if (type === "doctor_accept_appointment") return `${providerLabel} reviewed`;
  if (type === "admin_reschedule") return "Rescheduled by admin";
  if (type === "appointment_approved") return "Approved";
  if (type === "appointment_completed") return "Completed";
  if (type === "appointment_visit_outcome") return `Recorded by ${providerLabel}`;
  return "";
}

/** Human copy for system messages from `pushSystemMessage(..., "appointment_visit_outcome", …)` (doctor/hospital visit log). */
export function visitOutcomePatientDetail(p, providerLabel) {
  const pl = providerLabel === "Hospital" ? "hospital" : "doctor";
  if (p.patientAttended === false) {
    return `Your ${pl} recorded that you did not attend this scheduled visit. If that’s wrong, contact the clinic or HealBid support.`;
  }
  if (p.patientAttended === true && p.procedureProceeding === false) {
    return `Your ${pl} noted you attended, but the booked procedure did not go ahead as planned. Please follow up with them for next steps.`;
  }
  if (p.patientAttended === true && p.procedureProceeding === true) {
    const booked = toDisplayAmount(p.bookedPriceInr);
    const final = toDisplayAmount(p.finalAgreedPriceInr);
    if (p.samePrice === true && booked != null) {
      return `The visit went ahead as planned. The agreed amount matches your booked quote (${fmtINR(booked)}).`;
    }
    if (p.samePrice === false && booked != null && final != null) {
      const reason = String(p.priceChangeReason || "").trim();
      const tail = reason.length >= 8 ? ` Reason from ${pl}: ${reason}` : "";
      return `The visit went ahead with a revised agreed amount (${fmtINR(final)}; booked quote was ${fmtINR(booked)}).${tail}`;
    }
    return `Your ${pl} recorded that the visit took place and the procedure went ahead as planned.`;
  }
  return `Your ${pl} added a visit update. If anything looks off, contact support.`;
}

/**
 * Rich card for messageType === "system" in patient ↔ provider chats.
 */
export function PatientChatSystemCard({
  message,
  when,
  providerLabel = "Doctor",
  timelineLayout = false,
  /** When true and this card is the opening bid, show a muted “window ended” treatment. */
  openingBidExpired = false,
}) {
  const p = message?.payload || {};
  const type = String(p.type || "");
  const isLedger = BID_LEDGER_PAYLOAD_TYPES.has(type);
  const isVisitOutcome = type === "appointment_visit_outcome";
  const visitOutcomeOk = isVisitOutcome && p.patientAttended === true && p.procedureProceeding === true;
  const visitOutcomeWarn =
    isVisitOutcome &&
    (p.patientAttended === false || (p.patientAttended === true && p.procedureProceeding === false));
  const isSuccess =
    type === "appointment_approved" ||
    type === "appointment_completed" ||
    type === "patient_accept" ||
    type === "provider_accept" ||
    visitOutcomeOk;
  const isWarn = type === "patient_reject" || type === "provider_reject" || visitOutcomeWarn;

  let detail = "";
  if (type === "initial_bid") detail = `${fmtINR(p.amount)}`;
  else if (type === "reverse_bid") detail = `${providerLabel} offered ${fmtINR(p.amount)}`;
  else if (type === "patient_counter") detail = `You offered ${fmtINR(p.amount)}`;
  else if (type === "patient_accept")
    detail = toDisplayAmount(p.amount) != null ? `You agreed at ${fmtINR(p.amount)}.` : "You accepted the offer.";
  else if (type === "patient_reject") detail = "You declined the offer.";
  else if (type === "provider_accept")
    detail =
      toDisplayAmount(p.amount) != null
        ? `${providerLabel} accepted at ${fmtINR(p.amount)}.`
        : `${providerLabel} accepted your bid.`;
  else if (type === "provider_reject") detail = `${providerLabel} declined.`;
  else if (type === "appointment_requested") {
    detail = p.note
      ? `Note for ${providerLabel}: ${p.note}`
      : `We'll notify your ${providerLabel.toLowerCase()} and HealBid admin to review this request.`;
  } else if (type === "patient_booking_updated") {
    detail = `You picked a new published slot before ${providerLabel.toLowerCase()} responded. They and admin will see this time in the thread.`;
  } else if (type === "doctor_accept_appointment") {
    detail = p.message ? String(p.message) : `${providerLabel} accepted your visit request (pending admin approval).`;
  } else if (type === "doctor_reschedule") {
    detail = `Your ${providerLabel.toLowerCase()} suggested this new time. Confirm when you're ready, or continue the thread.`;
  } else if (type === "admin_reschedule") {
    detail =
      "HealBid admin picked a new slot from published availability. You will see the final visit time after verification.";
  } else if (type === "appointment_approved") {
    detail = "This visit is confirmed for the date and time below.";
  } else if (type === "appointment_completed") {
    detail = "Your provider marked this visit complete on HealBid. If you have billing or follow-up steps, check your appointments and messages.";
  } else if (type === "appointment_visit_outcome") {
    detail = visitOutcomePatientDetail(p, providerLabel);
  } else if (type === "slot_held") {
    const whenLine = [p.timeSlot, p.appointmentDate ? formatShortDate(p.appointmentDate) : ""].filter(Boolean).join(" · ");
    detail = whenLine
      ? "Saved as a reference from an earlier step. Use Book visit below when you are ready to request an appointment."
      : "Use Book visit below to choose a time and send your appointment request.";
  } else detail = message?.text || type || "";

  if (isLedger) {
    const side = type === "reverse_bid" ? "provider" : "patient";
    const title =
      type === "initial_bid"
        ? "Opening bid"
        : type === "reverse_bid"
          ? `${providerLabel} — reverse bid`
          : "Your counter-offer";
    const ledgerExpired = Boolean(openingBidExpired) && type === "initial_bid";
    return (
      <div
        className={`pch-ledger pch-ledger--${side}${timelineLayout ? " pch-ledger--timeline" : ""}${
          ledgerExpired ? " pch-ledger--expired" : ""
        }`.trim()}
        role="status"
      >
        <div className="pch-ledger__inner">
          <div className="pch-ledger__titleStack">
            <span className="pch-ledger__title">{title}</span>
            {ledgerExpired ? (
              <span className="pch-ledger__expiredTag" title="The reply window for a first response has passed.">
                Window ended
              </span>
            ) : null}
          </div>
          <span className="pch-ledger__amt">
            {p.amount != null ? (
              <InrDisplayParts
                amount={p.amount}
                currencyClassName="pch-ledger__currency"
                figureClassName="pch-ledger__figure"
              />
            ) : (
              "—"
            )}
          </span>
        </div>
        {!timelineLayout && when ? (
          <time className="pch-ledger__time" dateTime={message?.createdAt}>
            {when}
          </time>
        ) : null}
      </div>
    );
  }

  const showAgreedAmt =
    (type === "provider_accept" || type === "patient_accept") && toDisplayAmount(p.amount) != null;

  const apptVisual =
    type === "appointment_requested" ||
    type === "patient_booking_updated" ||
    type === "doctor_reschedule" ||
    type === "admin_reschedule" ||
    type === "slot_held" ||
    type === "appointment_approved" ||
    type === "appointment_completed" ||
    type === "appointment_visit_outcome";

  const slotHeldSummary =
    type === "slot_held"
      ? [p.timeSlot, p.appointmentDate ? formatShortDate(p.appointmentDate) : ""].filter(Boolean).join(" · ")
      : "";

  const appointmentRequestedSummary =
    type === "appointment_requested" || type === "patient_booking_updated"
      ? [p.timeSlot, p.date ? formatShortDate(p.date) : ""].filter(Boolean).join(" · ")
      : "";

  const appointmentApprovedSummary =
    type === "appointment_approved" ? [p.timeSlot, p.date ? formatShortDate(p.date) : ""].filter(Boolean).join(" · ") : "";

  const rescheduleSummary =
    type === "doctor_reschedule" || type === "admin_reschedule"
      ? [p.proposedTimeSlot, p.proposedDate ? formatShortDate(p.proposedDate) : ""].filter(Boolean).join(" · ")
      : "";

  let visitOutcomeSummary = "";
  if (type === "appointment_visit_outcome" && toDisplayAmount(p.bookedPriceInr) != null) {
    if (
      p.patientAttended === true &&
      p.procedureProceeding === true &&
      p.samePrice === false &&
      toDisplayAmount(p.finalAgreedPriceInr) != null
    ) {
      visitOutcomeSummary = `${fmtINR(p.bookedPriceInr)} booked · ${fmtINR(p.finalAgreedPriceInr)} logged`;
    } else if (p.patientAttended !== false && toDisplayAmount(p.bookedPriceInr) != null) {
      visitOutcomeSummary = `Booked quote ${fmtINR(p.bookedPriceInr)}`;
    }
  }

  const summaryLine =
    slotHeldSummary ||
    appointmentRequestedSummary ||
    appointmentApprovedSummary ||
    rescheduleSummary ||
    visitOutcomeSummary ||
    "";
  const stateLabel = systemStateLabel(type, providerLabel);

  return (
    <div
      className={`pch-sys-card${isSuccess ? " pch-sys-card--ok" : ""}${isWarn ? " pch-sys-card--warn" : ""}${
        type === "appointment_requested" ||
        type === "patient_booking_updated" ||
        type === "doctor_reschedule" ||
        type === "admin_reschedule" ||
        type === "slot_held"
          ? " pch-sys-card--appt"
          : ""
      }${type === "appointment_approved" || type === "appointment_completed" || visitOutcomeOk
        ? " pch-sys-card--appt pch-sys-card--appt-done"
        : ""}${
        showAgreedAmt ? " pch-sys-card--agreed" : ""
      }${apptVisual ? " pch-sys-card--rich" : ""}${timelineLayout ? " pch-sys-card--timeline" : ""}`}
      role="status"
    >
      <div className="pch-sys-card__chrome" aria-hidden />
      <div className="pch-sys-card__inner">
        <div className="pch-sys-card__head">
          <div className="pch-sys-card__kicker">{systemHeadline(type, p)}</div>
          {stateLabel ? <span className="pch-sys-card__state">{stateLabel}</span> : null}
        </div>
        {summaryLine ? (
          <>
            <p className="pch-sys-card__summary">{summaryLine}</p>
            <p className="pch-sys-card__body pch-sys-card__body--sub">{detail}</p>
          </>
        ) : (
          <div className="pch-sys-card__body">{detail}</div>
        )}
        {showAgreedAmt ? (
          <div className="pch-sys-card__agreed-amt">
            <InrDisplayParts
              amount={p.amount}
              currencyClassName="pch-sys-card__agreed-currency"
              figureClassName="pch-sys-card__agreed-figure"
            />
          </div>
        ) : null}
        {!timelineLayout && when ? (
          <time className="pch-sys-card__time" dateTime={message?.createdAt}>
            {when}
          </time>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Admin-authored text in patient ↔ provider threads (patient-visible only).
 * Styled as an official notice so it does not read like a casual chat bubble from the doctor.
 */
export function PatientChatAdminNoticeCard({ message, when, timelineLayout = false }) {
  const body = String(message?.text || "").trim() || "Message from HealBid.";
  return (
    <div className={`pch-admin-notice${timelineLayout ? " pch-admin-notice--timeline" : ""}`} role="status">
      <div className="pch-admin-notice__chrome" aria-hidden />
      <div className="pch-admin-notice__inner">
        <div className="pch-admin-notice__head">
          <span className="pch-admin-notice__icon" aria-hidden>
            <ShieldCheck size={17} strokeWidth={2.25} />
          </span>
          <div className="pch-admin-notice__head-text">
            <div className="pch-admin-notice__kicker">HealBid care team</div>
            <p className="pch-admin-notice__sub">Official message about your request or visit</p>
          </div>
          <span className="pch-admin-notice__state">Admin message</span>
        </div>
        <p className="pch-admin-notice__body">{body}</p>
        {!timelineLayout && when ? (
          <time className="pch-admin-notice__time" dateTime={message?.createdAt}>
            {when}
          </time>
        ) : null}
      </div>
    </div>
  );
}
